/** 預設提款幣種（相容舊流程） */
export const DEFAULT_WITHDRAW_CURRENCY = 'USDT';

export function buildBoundChannelSet(addresses) {
  return new Set((addresses || []).map((item) => item.channelType).filter(Boolean));
}

/** 已綁定且支付通道仍開啟的提款選項 */
export function listWithdrawableOptions(addresses, channels) {
  const channelByCode = new Map();
  for (const channel of channels || []) {
    if (channel?.code && !channelByCode.has(channel.code)) {
      channelByCode.set(channel.code, channel);
    }
  }
  return (addresses || [])
    .filter((address) => channelByCode.has(address.channelType))
    .map((address) => ({
      ...address,
      channel: channelByCode.get(address.channelType),
      optionId: String(address.id),
    }));
}

export function resolveWithdrawAssetType(option) {
  const fromAddress = option?.paymentCurrencyType;
  const type = typeof fromAddress === 'string'
    ? fromAddress
    : fromAddress?.name || fromAddress?.code;
  if (type) {
    return type;
  }
  return resolveBindCurrencyType(option?.channel);
}

export function isBankWithdrawOption(option) {
  return resolveWithdrawAssetType(option) === 'BANK';
}

export function isCryptoWithdrawOption(option) {
  return resolveWithdrawAssetType(option) === 'CRYPTO';
}

export function resolveWithdrawPickerLabel(option) {
  if (isBankWithdrawOption(option)) {
    return option.channel?.channelName || option.channel?.label || '銀行卡';
  }
  const currency = option.channel?.currency || option.channelType;
  const protocol = option.channel?.protocol;
  if (protocol) {
    return `${currency} (${protocol})`;
  }
  return option.channel?.label || currency || option.channelType || '';
}

export function resolveWithdrawCurrencyTitle(option) {
  if (isBankWithdrawOption(option)) {
    return '銀行卡';
  }
  return option.channel?.currency || option.channelType || DEFAULT_WITHDRAW_CURRENCY;
}

export function resolveWithdrawAddressFieldLabel(option, t) {
  if (isBankWithdrawOption(option)) {
    return t('withdraw.bankCard');
  }
  if (resolveWithdrawAssetType(option) === 'FIAT') {
    return t('withdraw.bindEwalletAccount');
  }
  return t('withdraw.walletAddress');
}

export function resolveWithdrawSelectorSubtitle(option) {
  if (isBankWithdrawOption(option)) {
    return option.bankName || option.channel?.channelName || '—';
  }
  return option.channel?.protocol || '—';
}

export function resolveWithdrawCurrencyName(option) {
  return option.channel?.currency || DEFAULT_WITHDRAW_CURRENCY;
}

export function resolveWithdrawMaxAmount(option, balance) {
  const available = Number(balance) || 0;
  const channelMax = Number(option?.channel?.withdrawalMax);
  if (channelMax > 0) {
    return Math.min(available, channelMax);
  }
  return available;
}

/**
 * 解析提款手續費比例。
 * 通道 withdrawalFee 為百分比（1 = 1%）；全域 withdrawFee 為比例（0.01 = 1%）。
 * 通道費率 > 0 時優先於全域設定。
 */
export function resolveWithdrawFeeRate(option, globalWithdrawFee = 0) {
  const channelFee = Number(option?.channel?.withdrawalFee);
  if (channelFee > 0) {
    return channelFee / 100;
  }
  const globalFee = Number(globalWithdrawFee);
  return globalFee > 0 ? globalFee : 0;
}

export function calcWithdrawFee(amount, feeRate = 0) {
  const value = Number(amount) || 0;
  const rate = Number(feeRate) || 0;
  if (value <= 0 || rate <= 0) {
    return 0;
  }
  return value * rate;
}

/** 尚未綁定的提款通道（線上支付通道，同鏈路代碼只顯示一筆） */
export function listUnboundChannels(channels, addresses) {
  const bound = buildBoundChannelSet(addresses);
  const seen = new Set();
  return (channels || []).filter((item) => {
    if (!item?.code || bound.has(item.code) || seen.has(item.code)) {
      return false;
    }
    seen.add(item.code);
    return true;
  });
}

export function resolveChannelLabel(channel) {
  if (!channel) {
    return '';
  }
  if (channel.channelName) {
    return channel.channelName;
  }
  if (channel.label) {
    return channel.label;
  }
  const parts = [channel.name, channel.protocol].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : channel.code || '';
}

/** 已綁定地址顯示名稱 */
export function resolveBoundAddressLabel(address, channels) {
  if (!address) {
    return '';
  }
  const matched = (channels || []).find((item) => item.code === address.channelType);
  if (matched) {
    return resolveChannelLabel(matched);
  }
  return address.channelType || '';
}

export function maskWalletAddress(address) {
  if (!address || address.length < 12) {
    return address || '';
  }
  return `${address.slice(0, 6)}...${address.slice(-6)}`;
}

/** 依分類過濾已綁定地址 */
export function filterAddressesByCategory(addresses, channels) {
  const channelCodes = new Set((channels || []).map((item) => item.code).filter(Boolean));
  if (channelCodes.size === 0) {
    return [];
  }
  return (addresses || []).filter((item) => channelCodes.has(item.channelType));
}

/** 依 coin_pay_type 推導綁定表單類型 */
export function resolveBindCurrencyType(channel) {
  if (!channel?.assetClass) {
    return 'CRYPTO';
  }
  if (channel.assetClass === 'FIAT_BANK') {
    return 'BANK';
  }
  if (channel.assetClass === 'FIAT_EWALLET') {
    return 'FIAT';
  }
  return 'CRYPTO';
}

/** 是否為銀行卡綁定表單 */
export function isBankBindForm(channel) {
  return resolveBindCurrencyType(channel) === 'BANK';
}

/** 是否為電子支付綁定表單（帳號） */
export function isEwalletBindForm(channel) {
  return resolveBindCurrencyType(channel) === 'FIAT';
}
